import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Autocomplete, Box, Button, TextField } from "@mui/material";
import {
  Competition,
  Player,
  Season,
  ShirtType,
  Team,
} from "../../../services/shirts/types";
import ShirtModelService from "../../../services/shirts/shirtModelService";
import { Dictionary } from "../../../types/types";
import BackdropLoaderService from "../../../components/BackdropLoader/backdropLoaderService";
import ShirtsService from "../../../services/shirts/shirtsService";
import SearchIcon from "@mui/icons-material/Search";

interface FiltersProps {
  setShirts: Function;
  page: number;
  setLastPage: Function;
  itemsPerPage: number;
  setPage: Function;
}

const Filters = ({
  setShirts,
  page,
  setLastPage,
  itemsPerPage,
  setPage,
}: FiltersProps) => {
  const [player, setPlayer] = useState<Player | null>(null);
  const [team, setTeam] = useState<Team | undefined>(undefined);
  const [competition, setCompetition] = useState<Competition | undefined>(
    undefined
  );
  const [season, setSeason] = useState<Season | undefined>(undefined);
  const [shirtType, setShirtType] = useState<ShirtType | null>(null);

  const [competitionOptions, setCompetitionOptions] = useState<Competition[]>();
  const [typeOptions, setTypeOptions] = useState<Array<ShirtType> | undefined>(
    undefined
  );
  const [seasonOptions, setSeasonOptions] = useState<Array<Season> | undefined>(
    undefined
  );
  const [teamOptions, setTeamOptions] = useState<Array<Team> | undefined>(
    undefined
  );
  const [playerOptions, setPlayerOptions] = useState<Array<Player> | undefined>(
    undefined
  );

  const {
    getAllPlayersByTeamAndSeasonAndCompetition,
    getTeamsByCompetitionAndSeason,
    getAllSeasons,
    getAllTypes,
    getAllCompetitions,
  } = ShirtModelService();
  const { open: openBDL, close: closeBDL } = BackdropLoaderService();
  const { searchShirtsPublic } = ShirtsService();

  const { playerId, teamId } = useParams();

  const search = () => {
    openBDL();
    let params: Dictionary<string | number> = {};
    if (team) {
      params["team_id"] = team.id;
    }
    if (competition) {
      params["competition_id"] = competition.id;
    }
    if (player) {
      params["player_id"] = player.id;
    }
    if (season) {
      params["season_id"] = season.id;
    }
    if (shirtType) {
      params["type_id"] = shirtType.id;
    }
    if (page) {
      params["page"] = page > 0 ? page : 1;
    }

    params["perPage"] = itemsPerPage;

    params["order_by"] = "created_at";

    searchShirtsPublic(params).then((results) => {
      setShirts(results.shirts);
      setLastPage(results.lastPage);
      closeBDL();
    });
  };

  const searchByTeamId = (teamId: string) => {
    openBDL();
    let params: Dictionary<string | number> = {};
    params["team_id"] = teamId;
    params["perPage"] = itemsPerPage;
    params["page"] = 1;

    searchShirtsPublic(params).then((results) => {
      setShirts(results.shirts);
      setLastPage(results.lastPage);
      closeBDL();
    });
  };

  const searchByPlayerId = (playerId: string) => {
    openBDL();
    let params: Dictionary<string | number> = {};
    params["player_id"] = playerId;
    params["perPage"] = itemsPerPage;
    params["page"] = 1;

    searchShirtsPublic(params).then((results) => {
      setShirts(results.shirts);
      setLastPage(results.lastPage);
      closeBDL();
    });
  };

  const handleSearchBtnClick = () => {
    openBDL();
    let params: Dictionary<string | number> = {};
    if (team) {
      params["team_id"] = team.id;
    }
    if (competition) {
      params["competition_id"] = competition.id;
    }
    if (player) {
      params["player_id"] = player.id;
    }
    if (season) {
      params["season_id"] = season.id;
    }
    if (shirtType) {
      params["type_id"] = shirtType.id;
    }

    params["page"] = 1;
    params["perPage"] = itemsPerPage;
    params["order_by"] = "created_at";

    setPage(1);

    searchShirtsPublic(params).then((results) => {
      setShirts(results.shirts);
      setLastPage(results.lastPage);
      closeBDL();
    });
  };

  useEffect(() => {
    if (playerId !== undefined || teamId !== undefined) {
      if (playerId !== undefined) {
        searchByPlayerId(playerId);
      } else if (teamId !== undefined) {
        searchByTeamId(teamId);
      }
    } else if (itemsPerPage && page > 0) {
      search();
    }
  }, [playerId, teamId, itemsPerPage, page]);

  useEffect(() => {
    getAllTypes().then((types) => {
      setTypeOptions(types);
    });
    getAllSeasons().then((seasons) => {
      if (seasons) {
        const sortedSeasons = seasons.sort(
          (a, b) => parseInt(b.start_year) - parseInt(a.start_year)
        );
        setSeasonOptions(sortedSeasons);
      }
    });
    getAllCompetitions().then((res) => {
      if (res && res?.length > 0) {
        const sortedComp = res.sort((a, b) => {
          if (a.name === "National Teams") return -1;
          if (b.name === "National Teams") return 1;
          return a.name.localeCompare(b.name);
        });
        setCompetitionOptions(sortedComp);
      }
    });
  }, []);

  useEffect(() => {
    if (competition !== undefined && season !== undefined) {
      //filter teams
      getTeamsByCompetitionAndSeason(competition.id, season.id).then(
        (teams) => {
          if (teams) {
            const sortedTeams = teams.sort((a, b) =>
              a.name.localeCompare(b.name)
            );
            setTeamOptions(sortedTeams);
          }
        }
      );
    }
  }, [competition, season]);

  useEffect(() => {
    setPlayerOptions(undefined);
    if (
      competition !== undefined &&
      team !== undefined &&
      season !== undefined
    ) {
      //filter players
      getAllPlayersByTeamAndSeasonAndCompetition(
        competition.id,
        season.id,
        team.id
      ).then((players) => {
        if (players && (players?.length ?? 0) > 0) {
          const sortedPlayers = players.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setPlayerOptions(sortedPlayers);
        } else {
          setPlayerOptions(undefined);
        }
      });
    }
  }, [team, season, competition]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        padding: "1rem 0rem",
      }}
      aria-label="Search in Collections-FilterBox"
    >
      <Box sx={filterItemBoxStyle} boxSizing={"border-box"}>
        <Autocomplete
          fullWidth
          disablePortal
          getOptionLabel={(option) => {
            if (option.country) {
              return `${option.name} - ${option.country}`;
            } else {
              return option.name;
            }
          }}
          getOptionKey={(option: Competition) => option.id}
          options={competitionOptions ?? []}
          sx={autocompleteStyle}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Competition"
              sx={tfMainStyle}
              InputLabelProps={tfLabelProps}
            />
          )}
          value={competition}
          onChange={(event: any, newValue: Competition | null) => {
            if (newValue) {
              setCompetition(newValue);
            } else {
              setCompetition(undefined);
            }
          }}
        />
      </Box>
      <Box boxSizing={"border-box"} sx={filterItemBoxStyle}>
        <Autocomplete
          disablePortal
          getOptionLabel={(option: Season) =>
            option.start_year + "/" + option.end_year
          }
          options={seasonOptions ?? []}
          sx={autocompleteStyle}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Season/Period"
              sx={tfMainStyle}
              InputLabelProps={tfLabelProps}
            />
          )}
          value={season}
          onChange={(event: any, newValue: Season | null) => {
            if (newValue) {
              setSeason(newValue);
            } else {
              setSeason(undefined);
            }
          }}
        />
      </Box>
      <Box boxSizing={"border-box"} sx={filterItemBoxStyle}>
        <Autocomplete
          disablePortal
          getOptionLabel={(option: Team) => option.name}
          options={teamOptions ?? []}
          sx={autocompleteStyle}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Club/Nation"
              sx={tfMainStyle}
              InputLabelProps={tfLabelProps}
            />
          )}
          value={team}
          onChange={(event: any, newValue: Team | null) => {
            if (newValue) {
              setTeam(newValue);
            } else {
              setTeam(undefined);
            }
          }}
          disabled={!teamOptions}
        />
      </Box>
      <Box boxSizing={"border-box"} sx={filterItemBoxStyle}>
        <Autocomplete
          disablePortal
          getOptionLabel={(option: Player) =>
            option.alias && option.alias !== option.name
              ? option.name + ' "' + option.alias + '"'
              : option.name
          }
          options={playerOptions ?? []}
          sx={autocompleteStyle}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Player"
              sx={tfMainStyle}
              InputLabelProps={tfLabelProps}
            />
          )}
          value={player}
          onChange={(event: any, newValue: Player | null) => {
            setPlayer(newValue);
          }}
          disabled={!playerOptions}
        />
      </Box>
      <Box boxSizing={"border-box"} sx={filterItemBoxStyle}>
        <Autocomplete
          disablePortal
          getOptionLabel={(option: ShirtType) => option.name}
          options={typeOptions ?? []}
          sx={autocompleteStyle}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Type of shirt"
              sx={tfMainStyle}
              InputLabelProps={tfLabelProps}
            />
          )}
          value={shirtType}
          onChange={(event: any, newValue: ShirtType | null) => {
            setShirtType(newValue);
          }}
        />
      </Box>
      <Box sx={filterItemBoxStyle}>
        <Button
          fullWidth
          variant="darkBlue"
          onClick={handleSearchBtnClick}
          startIcon={<SearchIcon sx={{ color: "#fff" }} />}
          sx={{
            fontSize: "1rem",
            paddingTop: "13px",
            paddingBottom: "13px",
            borderRadius: "8px",
            textTransform: "capitalize",
          }}
        >
          Search
        </Button>
      </Box>
    </Box>
  );
};

export default Filters;

const filterItemBoxStyle = {
  width: {
    xs: "calc((100% - 20px) / 2)",
    sm: "calc((100% - 45px) / 3)",
    md: "calc((100% - 120px) / 6)",
  },
  padding: {
    xs: "0.5rem 0px",
    sm: "1rem 0px",
    md: "0px",
  },
};

const autocompleteStyle = {
  fontWeight: 700,
  backgroundColor: "white",
  borderRadius: "10px",
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "none",
    },
    "&:hover fieldset": {
      border: "none",
    },
    "&.Mui-focused fieldset": {
      border: "none",
    },
  },
};

const tfLabelProps = {
  sx: {
    fontSize: "1rem",
    fontWeight: 700,
    "&.MuiOutlinedInput-notchedOutline": {
      fontSize: "28px",
      border: "0px !important",
    },
  },
};

const tfMainStyle = {
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "0px",
  },
};
